import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import Stack from 'react-bootstrap/Stack';
import { TbLayoutSidebarLeftExpand, TbLayoutSidebarRightExpand } from 'react-icons/tb';
import { FaFileCsv } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { useAuth } from '../../providers/auth-provider';
import LoadingSpinner from '../common/loading-spinner';
import {
  CUTOFF_R,
  CUTOFF_DNR,
  CUTOFF_HR,
  CUTOFF_RWC,
  getRecommendationCutoff,
  getNineBoxValue,
  getNineBoxSortValue,
  getNineBoxRecommendationCutoff,
} from '../client-dashboard/candidate-scoring-util';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import {
  getCandidateLoyaltyText,
  getCandidatePerformanceText,
  getCandidateValuesText,
} from '../reports/shared/report-util';
import { buildMetadataColumn } from '../shared/grid/grid-metadata';
import { getClientConfiguration } from '../../api/client';
import { getMetadataAttributes } from '../../api/metadata';
import { getPercentileAttributes, getScorecardMetrics, isSelectStaffing } from '../../api/dashboards-api';
import {
  filtersExist,
  getFilterKeys,
  getOpeningType,
  LEADERSHIP_OPENING,
  STAFFING_ONSITE_OPENING,
} from '../shared/filters/filter-common';
import { DashboardStaffingFilter } from '../client-dashboard/dashboard-staffing-filter';

const getRecommendationCellCutoff = (params) => {
  const candidate = params.data;
  const nineBox = params.colDef.nineBox;
  if (!nineBox) {
    return getRecommendationCutoff(candidate);
  } else {
    return getNineBoxSortValue(candidate);
  }
};
const getRecommendationText = (params) => {
  const candidate = params.data;
  const nineBox = params.colDef.nineBox;
  if (!nineBox) {
    const cutoff = getRecommendationCutoff(candidate);
    switch (cutoff) {
      case CUTOFF_R:
        return 'Recommend';
      case CUTOFF_HR:
        return 'Highly Recommend';
      case CUTOFF_RWC:
        return 'Recommend With Caution';
      default:
        return 'Do Not Recommend';
    }
  } else {
    const cutoff = getNineBoxValue(candidate);
    switch (cutoff) {
      case 0:
        return 'Loyal Poor Performer';
      case 1:
        return 'Loyal Good Performer';
      case 2:
        return 'High Overall Potential';
      case 3:
        return 'Disengaged Poor Performer';
      case 4:
        return 'Disengaged Good Performer';
      case 5:
        return 'Disengaged Great Performer';
      case 6:
        return 'Apathetic Low Potential';
      case 7:
        return 'Apathetic Good Performer';
      default:
        return 'Apathetic Great Performer';
    }
  }
};

const getCandidateCellValue = (params) => params.data[params.colDef.colId];

const getCandidatePercentilesCellValue = (params) => params.data.percentiles[params.colDef.colId];
const getCompetenciesValue = (candidate) => {
  return candidate.selfCompetencies.scores.overallLeadership;
};

const getCompetenciesCellValue = (params) => {
  return getCompetenciesValue(params.data);
};

const formatCompetenciesColumn = (candidate) => {
  const competencies = getCompetenciesValue(candidate);

  let competenciesStyle = 'compare-candidate-dot';
  if (competencies >= 4.25) {
    competenciesStyle += ' compare-dot-r';
  } else if (competencies >= 3.0) {
    competenciesStyle += ' compare-dot-rwc';
  } else {
    competenciesStyle += ' compare-dot-dnr';
  }

  return (
    <div className="d-flex h-100 align-content-center">
      <div className={competenciesStyle} />
    </div>
  );
};
const getRecommendationStyle = (params) => {
  const candidate = params.data;
  const nineBox = params.colDef.nineBox;

  const classes = [];
  let cutoff;

  if (!nineBox) {
    cutoff = getRecommendationCutoff(candidate);
    classes.push('compare-recommendation');
  } else {
    cutoff = getNineBoxRecommendationCutoff(candidate);
    classes.push('compare-overall');
  }
  switch (cutoff) {
    case CUTOFF_HR:
      classes.push('compare-hr');
      break;
    case CUTOFF_DNR:
      classes.push('compare-dnr');
      break;
    case CUTOFF_RWC:
      classes.push('compare-rwc');
      break;
    default:
      classes.push('compare-r');
  }

  return classes;
};

const compareRecommendation = (valueA, valueB, _isDescending) => {
  if (valueA === valueB) {
    return 0;
  }

  return valueA > valueB ? 1 : -1;
};

const getValueStyle = (value) => {
  const classes = [];
  classes.push('compare-value');

  if (value) {
    if (value <= 33) {
      classes.push('compare-dnr');
    } else if (value >= 66) {
      classes.push('compare-hr');
    } else {
      classes.push('compare-rwc');
    }
  }

  return classes;
};

const getCsvPercentileValue = (value) => {
  if (value <= 33) {
    return 'Red';
  } else if (value >= 66) {
    return 'Green';
  }

  return 'Yellow';
};

const getDotStyle = (value) => {
  let dotStyle = 'compare-candidate-dot';

  if (value <= 33) {
    dotStyle += ' compare-dot-dnr';
  } else if (value >= 66) {
    dotStyle += ' compare-dot-r';
  } else {
    dotStyle += ' compare-dot-rwc';
  }

  return dotStyle;
};

const formatDotColumn = (value) => (
  <div className="d-flex h-100 align-content-center">
    <div className={getDotStyle(value)} />
  </div>
);

const getInterviewStyle = (value) => {
  let interviewStyle = 'compare-candidate-dot';

  if (value) {
    if (value >= 23) {
      interviewStyle += ' compare-dot-r';
    } else if (value >= 19) {
      interviewStyle += ' compare-dot-rwc';
    } else {
      interviewStyle += ' compare-dot-dnr';
    }
  }

  return interviewStyle;
};

const formatInterviewColumn = (value) => (
  <div className="d-flex h-100 align-content-center">
    <div className={getInterviewStyle(value)} />
  </div>
);

const defaultColumnDefinition = {
  flex: 1,
  sortable: true,
};

let baseColumnDefinitions = [
  {
    colId: 'name',
    valueGetter: getCandidateCellValue,
    headerName: 'Candidate Name',
    sort: 'asc',
    filter: 'agTextColumnFilter',
    cellClass: 'compare-text-value',
    filterParams: {
      filterOptions: ['contains', 'equals'],
      suppressAndOrCondition: true,
    },
    minWidth: 180,
  },
  {
    colId: 'recommendation',
    flex: 2,
    headerClass: 'ag-center-header',
    comparator: (valueA, valueB, _nodeA, _nodeB, isDescending) => compareRecommendation(valueA, valueB, isDescending),
    valueGetter: (params) => getRecommendationCellCutoff(params),
    valueFormatter: (params) => getRecommendationText(params),
    cellClass: (params) => getRecommendationStyle(params),
    minWidth: 180,
  },
  {
    colId: 'performance',
    headerName: 'Performance Header',
    valueGetter: getCandidatePercentilesCellValue,
    valueFormatter: (params) => `${params.data.percentiles.performance}%`,
    cellClass: (params) => getValueStyle(params.data.percentiles.performance),
    minWidth: 180,
    headerClass: 'ag-center-header',
    wrapHeaderText: true,
  },
  {
    colId: 'loyalty',
    headerName: 'Loyalty Header',
    valueGetter: getCandidatePercentilesCellValue,
    valueFormatter: (params) => `${params.data.percentiles.loyalty}%`,
    cellClass: (params) => getValueStyle(params.data.percentiles.loyalty),
    minWidth: 180,
    headerClass: 'ag-center-header',
    wrapHeaderText: true,
  },
];

const additionalColumnDefinitions = [
  {
    colId: 'personality',
    headerName: 'Personality',
    headerClass: 'ag-center-header',
    valueGetter: getCandidatePercentilesCellValue,
    cellRenderer: (params) => formatDotColumn(params.data.percentiles.personality),
    minWidth: 120,
  },
  {
    colId: 'values',
    headerName: `Values Header`,
    headerClass: 'ag-center-header',
    valueGetter: getCandidatePercentilesCellValue,
    cellRenderer: (params) => formatDotColumn(params.data.percentiles.values),
    minWidth: 100,
    wrapHeaderText: true,
  },
  {
    colId: 'acumen',
    headerName: 'Acumen',
    headerClass: 'ag-center-header',
    valueGetter: getCandidatePercentilesCellValue,
    cellRenderer: (params) => formatDotColumn(params.data.percentiles.acumen),
    minWidth: 100,
  },
  {
    colId: 'judgment',
    headerName: 'Judgment',
    headerClass: 'ag-center-header',
    valueGetter: getCandidatePercentilesCellValue,
    cellRenderer: (params) => formatDotColumn(params.data.percentiles.judgment),
    minWidth: 120,
  },
  {
    colId: 'experience',
    headerName: 'Experience',
    headerClass: 'ag-center-header',
    valueGetter: getCandidatePercentilesCellValue,
    cellRenderer: (params) => formatDotColumn(params.data.percentiles.experience),
    minWidth: 120,
  },
];

const CSV_PERCENTILE_COLUMN_IDS = ['personality', 'values', 'acumen', 'judgment', 'experience'];

const additionalCandidateColumnDefinitions = [
  {
    colId: 'interview',
    headerName: 'Interview',
    headerClass: 'ag-center-header',
    valueGetter: getCandidateCellValue,
    cellRenderer: (params) => formatInterviewColumn(params.data.interviewCompetencies?.scores.overall),
    minWidth: 100,
  },
];

const additionalLeadershipColumnDefinitions = [
  {
    colId: 'competencies',
    headerName: 'Competencies',
    headerClass: 'ag-center-header',
    valueGetter: getCompetenciesCellValue,
    cellRenderer: (params) => formatCompetenciesColumn(params.data),
    minWidth: 140,
  },
];

const expandedColumnDefinitions = baseColumnDefinitions.concat(additionalColumnDefinitions);

const updateColumnDefinitions = (columnDefinitions, isDevelop, isStaffing) => {
  const updatedColumnDefinitions = [...columnDefinitions];
  const percentileAttributes = getPercentileAttributes();
  let acumenIndex = 0;
  for (let c = 0; c < updatedColumnDefinitions.length; c++) {
    const def = updatedColumnDefinitions[c];
    const colId = def['colId'];
    switch (colId) {
      case 'performance':
        def['headerName'] = getCandidatePerformanceText(!isDevelop);
        def['valueGetter'] = (params) => params.data.percentiles[getPercentileAttributes().performance];
        def['valueFormatter'] = (params) => `${params.data.percentiles[getPercentileAttributes().performance]}%`;
        def['cellClass'] = (params) => getValueStyle(params.data.percentiles[getPercentileAttributes().performance]);
        break;
      case 'loyalty':
        def['headerName'] = getCandidateLoyaltyText(!isDevelop);
        def['valueGetter'] = (params) => params.data.percentiles[getPercentileAttributes().loyalty];
        def['valueFormatter'] = (params) => `${params.data.percentiles[getPercentileAttributes().loyalty]}%`;
        def['cellClass'] = (params) => getValueStyle(params.data.percentiles[getPercentileAttributes().loyalty]);
        break;
      case 'personality':
        def['valueGetter'] = (params) => params.data.percentiles[percentileAttributes.personality];
        def['cellRenderer'] = (params) => formatDotColumn(params.data.percentiles[percentileAttributes.personality]);
        break;
      case 'values':
        def['headerName'] = getCandidateValuesText(!isDevelop, true);
        def['valueGetter'] = (params) => params.data.percentiles[percentileAttributes.values];
        def['cellRenderer'] = (params) => formatDotColumn(params.data.percentiles[percentileAttributes.values]);
        break;
      case 'judgment':
        def['valueGetter'] = (params) => params.data.percentiles[percentileAttributes.judgment];
        def['cellRenderer'] = (params) => formatDotColumn(params.data.percentiles[percentileAttributes.judgment]);
        break;
      case 'experience':
        def['valueGetter'] = (params) => params.data.percentiles[percentileAttributes.experience];
        def['cellRenderer'] = (params) => formatDotColumn(params.data.percentiles[percentileAttributes.experience]);
        break;
      case 'acumen':
        acumenIndex = c;
        break;
    }
  }

  if (acumenIndex !== 0 && isStaffing) {
    const openingType = getOpeningType();
    // TODO Determine what this does with Leadership type
    updatedColumnDefinitions.splice(acumenIndex, openingType === STAFFING_ONSITE_OPENING ? 1 : 2);
  }

  return updatedColumnDefinitions;
};

function CandidateCompareContent() {
  const navigate = useNavigate();
  const { isDevelop } = useAuth();
  const auth = useAuth();
  const [initialized, setInitialized] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [showInterviewColumn, setShowInterviewColumn] = useState(false);
  const [currentColumns, setCurrentColumns] = useState([]);
  const [metadataColumnIds, setMetadataColumnIds] = useState([]);
  const [group1MetadataColumns] = useState([]);
  const [group2MetadataColumns] = useState([]);
  const [currentBaseColumns, setCurrentBaseColumns] = useState([]);
  const [currentExpandedColumns, setCurrentExpandedColumns] = useState([]);
  const gridRef = useRef();
  const isStaffing = isSelectStaffing();
  let openingType = getOpeningType();

  const openingTypeFilterChanged = () => {
    setCurrentBaseColumns(updateColumnDefinitions(baseColumnDefinitions, isDevelop(), isStaffing));
    setCurrentExpandedColumns(updateColumnDefinitions(expandedColumnDefinitions, isDevelop(), isStaffing));

    let updateFilter = false;
    const newOpeningType = getOpeningType();
    if (openingType === LEADERSHIP_OPENING || newOpeningType === LEADERSHIP_OPENING) {
      updateFilter = true;
    }

    openingType = newOpeningType;

    if (gridRef.current) {
      if (updateFilter) {
        gridRef.current.api.onFilterChanged();
      }
      gridRef.current.api.refreshCells();
    }
  };

  const goToCandidateProfile = (clientReference) => {
    navigate(`/reports/${clientReference}`);
  };

  const gridOptions = useMemo(
    () => ({
      suppressCellFocus: true,
      onCellClicked: (event) => {
        goToCandidateProfile(event.data.reference);
      },
    }),
    []
  );

  const csvExport = () => {
    if (gridRef.current) {
      gridRef.current.api.exportDataAsCsv({
        allColumns: true,
        processCellCallback: (params) => {
          if (CSV_PERCENTILE_COLUMN_IDS.includes(params.column.getId())) {
            return getCsvPercentileValue(params.value);
          } else if (params.column.getId() === 'recommendation') {
            return getRecommendationText({ data: params.node.data, colDef: params.column.getColDef() });
          } else if (params.node && metadataColumnIds.includes(params.column.getId())) {
            const candidate = params.node.data;
            const metadataKey = params.column.getId();
            const colDef = params.column.getColDef();

            const metadata = candidate.metadata[metadataKey];
            if (metadata) {
              return metadata.item[colDef.field];
            }

            return '';
          }

          return params.value;
        },
        fileName: `${auth.slug}-compare.csv`,
      });
    }
  };

  const isStaffingFilterPresent = useCallback(() => {
    return isStaffing;
  }, []);

  const doesStaffingFilterPass = (node) => {
    if (node.data?.percentiles) {
      if (openingType === LEADERSHIP_OPENING) {
        return node.data.percentiles.performance !== undefined;
      } else {
        return node.data.percentiles.onsitePerformance !== undefined;
      }
    }
    return true;
  };

  const expandClicked = (newValue) => {
    if (newValue) {
      setCurrentColumns(getColumnDefinitions(true));
    } else {
      setCurrentColumns(getColumnDefinitions());
    }
    setExpanded(newValue);
    if (gridRef.current) {
      gridRef.current.columnApi.resetColumnState();
    }
  };

  useEffect(() => {
    if (!initialized) {
      getScorecardMetrics(auth).then((response) => {
        setCandidates(response.candidates);
        setShowInterviewColumn(false);
        for (const candidate of response.candidates) {
          if (candidate.interviewCompetencies) {
            setShowInterviewColumn(true);
            break;
          }
        }
        const columnIds = [];
        const configuration = getClientConfiguration();
        let metadataSpecs;
        if (isDevelop()) {
          metadataSpecs = configuration?.develop?.compare?.metadata;
        } else {
          metadataSpecs = configuration?.select?.compare?.metadata;
        }
        if (metadataSpecs?.length > 0) {
          getMetadataAttributes(auth)
            .then((clientAttributes) => {
              for (const metadataSpec of metadataSpecs) {
                const attributeId = `${metadataSpec.attribute}`;
                const attribute = clientAttributes[attributeId];
                if (attribute) {
                  columnIds.push(attribute.name);
                  if (metadataSpec.group === 1) {
                    group1MetadataColumns.push(buildMetadataColumn(clientAttributes, metadataSpec));
                  } else {
                    group2MetadataColumns.push(buildMetadataColumn(clientAttributes, metadataSpec));
                  }
                }
              }
              setMetadataColumnIds(columnIds);
            })
            .finally(() => {
              setExpanded(false);
              setInitialized(true);
            });
        } else {
          setExpanded(false);
          setInitialized(true);
        }
      });
    }
  }, [auth, initialized]);

  const getColumnDefinitions = (expanded) => {
    if (!expanded) {
      const metadataColumns = [...currentBaseColumns];

      if (group1MetadataColumns?.length > 0) {
        metadataColumns.splice(1, 0, ...group1MetadataColumns);
      }

      return metadataColumns;
    } else {
      let metadataColumns;
      if (isDevelop()) {
        metadataColumns = [...expandedColumnDefinitions.concat(additionalLeadershipColumnDefinitions)];
      } else if (showInterviewColumn) {
        metadataColumns = [...expandedColumnDefinitions.concat(additionalCandidateColumnDefinitions)];
      } else {
        metadataColumns = [...expandedColumnDefinitions];
      }
      if (group1MetadataColumns?.length > 0) {
        metadataColumns.splice(1, 0, ...group1MetadataColumns);
      }
      if (group2MetadataColumns?.length > 0) {
        metadataColumns.splice(metadataColumns.length, 0, ...group2MetadataColumns);
      }
      return metadataColumns;
    }
  };

  const onGridReady = (event) => {
    if (filtersExist()) {
      const keys = getFilterKeys();
      keys.forEach((key) => {
        event.api.getFilterInstance(key, (filterInstance) => {
          if (filterInstance) {
            event.api.onFilterChanged();
          }
        });
      });
    }
  };

  const onFilterChanged = (event) => {
    if (event.type === 'filterChanged' && event.columns.length > 0) {
      const eventKey = event.columns[0].colId;
      if (filtersExist()) {
        const keys = getFilterKeys();
        keys.forEach((key) => {
          if (key !== eventKey) {
            event.api.getFilterInstance(key, (filterInstance) => {
              if (filterInstance) {
                filterInstance.setModel(key);
              }
            });
          }
        });
      }
    }
  };

  useEffect(() => {
    setCurrentColumns(getColumnDefinitions(expanded));
  }, [currentBaseColumns, currentExpandedColumns]);

  useEffect(() => {
    if (initialized) {
      const config = getClientConfiguration();
      let compareConfig;
      if (isDevelop()) {
        compareConfig = config.develop.compare;
        baseColumnDefinitions[0]['headerName'] = 'Leader Name';
        if (baseColumnDefinitions[1]['colId'] === 'recommendation') {
          baseColumnDefinitions[1]['headerName'] = 'Overall';
          baseColumnDefinitions[1]['nineBox'] = true;
          if (compareConfig.hideRecommendation) {
            baseColumnDefinitions.splice(1, 1);
            expandedColumnDefinitions.splice(1, 1);
          }
        }
      } else {
        baseColumnDefinitions[1]['headerName'] = 'Recommendation';
      }

      setCurrentBaseColumns(updateColumnDefinitions(baseColumnDefinitions, isDevelop(), isStaffing));
      setCurrentExpandedColumns(updateColumnDefinitions(expandedColumnDefinitions, isDevelop(), isStaffing));
    }
  }, [showInterviewColumn, initialized]);

  return (
    <>
      {!initialized && <LoadingSpinner />}
      <div className="flex w-100 mx-auto bg-gray-200 pb-4 candidate-compare-container">
        {candidates.length > 0 && (
          <Stack
            className={`flex-column m-2 mx-auto ps-3 pe-3 pb-2 ${
              expanded || metadataColumnIds.length > 0
                ? 'compare-content-stack-expanded'
                : 'compare-content-stack-collapsed'
            }`}
          >
            <Stack direction="horizontal">
              {isSelectStaffing() && (
                <Stack direction="horizontal" className="text-smaller">
                  <div className="pe-2 text-bold">Opening Type</div>
                  <DashboardStaffingFilter staffingFilterUpdatedCallback={openingTypeFilterChanged} />
                </Stack>
              )}
              <Stack direction="horizontal" className="ms-auto me-0">
                {expanded && (
                  <Button className="bg-transparent border-0" onClick={() => csvExport()}>
                    <FaFileCsv size="2em" color="#0f4f73" />
                  </Button>
                )}
                <Button className="border-0 bg-white" onClick={() => expandClicked(!expanded)}>
                  {expanded && <TbLayoutSidebarRightExpand size="2em" />}
                  {!expanded && <TbLayoutSidebarLeftExpand size="2em" />}
                </Button>
              </Stack>
            </Stack>
            {initialized && (
              <div className="candidate-compare-table-container" style={{ height: '90%' }}>
                <div className="ag-theme-alpine" style={{ height: '90%', boxSizing: 'border-box' }}>
                  <AgGridReact
                    ref={gridRef}
                    onFirstDataRendered={onGridReady}
                    onFilterChanged={onFilterChanged}
                    rowData={candidates}
                    gridOptions={gridOptions}
                    columnDefs={currentColumns}
                    defaultColDef={defaultColumnDefinition}
                    isExternalFilterPresent={isStaffingFilterPresent}
                    doesExternalFilterPass={doesStaffingFilterPass}
                  />
                </div>
              </div>
            )}
          </Stack>
        )}
      </div>
    </>
  );
}

export default CandidateCompareContent;
